import { createRoot } from 'react-dom/client';
import { PublicClientApplication, EventType, type AccountInfo } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import { QueryClientProvider } from '@tanstack/react-query';
import App from './App';
import { msalConfig } from './lib/authConfig';
import { isMock } from './lib/mock';
import { isLocalAuth } from './lib/localAuth';
import { queryClient } from './lib/queryClient';
import { ApiProvider } from './lib/ApiContext';
import './styles.css';

// MSAL (@azure/msal-browser + @azure/msal-react) drives Entra ID sign-in via Authorization
// Code + PKCE. `MsalProvider` handles the /auth/callback redirect response automatically via
// `handleRedirectPromise()` (called internally on construction/initialize) — App.tsx renders
// a "Signing in…" state while `inProgress !== InteractionStatus.None`.
//
// In mock mode and local dev-auth mode, a PublicClientApplication is still constructed (so
// `useMsal()` never throws inside ApiContext/App) but is never actually exercised — no
// redirect is triggered, and ApiContext/apiClient short-circuit around it entirely (see
// src/lib/mock.ts, src/lib/localAuth.ts, src/lib/apiClient.ts).
const msalInstance = new PublicClientApplication(msalConfig);

msalInstance.addEventCallback((event) => {
  if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
    const account = (event.payload as { account?: AccountInfo }).account;
    if (account) msalInstance.setActiveAccount(account);
  }
});

async function bootstrap() {
  if (!isMock() && !isLocalAuth()) {
    await msalInstance.initialize();
    const result = await msalInstance.handleRedirectPromise();
    if (result?.account) msalInstance.setActiveAccount(result.account);
    else if (!msalInstance.getActiveAccount()) {
      const existing = msalInstance.getAllAccounts()[0];
      if (existing) msalInstance.setActiveAccount(existing);
    }
  }

  createRoot(document.getElementById('root')!).render(
    <MsalProvider instance={msalInstance}>
      <QueryClientProvider client={queryClient}>
        <ApiProvider>
          <App />
        </ApiProvider>
      </QueryClientProvider>
    </MsalProvider>,
  );
}

void bootstrap();
